import React, { useState, useEffect, useRef, useCallback } from "react";
import siteConfig from "../config/site";

/* ──────────────────────────────────────────────────────────────────────────
   Geo offer modal.

   Detects the visitor's country (via the /geo Netlify edge function) and, if
   they've landed on the "wrong" site for their location, offers to switch.
   It is an OFFER, never an automatic redirect: the page content is identical
   for every visitor and every crawler, and the modal is injected client-side
   only, so there is no cloaking or indexing risk.

   Which site the offer points at is driven by GATSBY_SITE via the shared
   site config, so both builds get the right pairing automatically.
   ────────────────────────────────────────────────────────────────────────── */

// Master switch. Both sites are live and signed off.
const GEO_ENABLED = true;

// Which site is this build? Driven by GATSBY_SITE via the shared site config.
const THIS_SITE = siteConfig.geoThisSite; // 'com' | 'ae'

const SITES = {
    // International (.com) site: offer the UAE site to visitors inside the UAE.
    com: {
        shouldOffer: (country) => country === 'AE',
        heading: 'Are you located in the United Arab Emirates?',
        body: 'You are currently viewing our international website, intended for clients outside the UAE. We have a dedicated UAE site that may be more appropriate for you.',
        goLabel: 'Go to our UAE site',
        stayLabel: 'Stay on this site',
        targetUrl: 'https://rscapitalglobal.ae/',
    },
    // UAE (.ae) site: offer the international site to visitors outside the UAE.
    ae: {
        shouldOffer: (country) => Boolean(country) && country !== 'AE',
        heading: 'Are you located outside the United Arab Emirates?',
        body: 'You are currently viewing our UAE website. We have an international website that may be more appropriate for you.',
        goLabel: 'Go to our international site',
        stayLabel: 'Stay on this site',
        targetUrl: 'https://rscapitalglobal.com/',
    },
};

const CFG = SITES[THIS_SITE];

const STORAGE_KEY = 'rscg_geo_choice';
const REMEMBER_DAYS = 30;

const alreadyChosen = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return false;
        return Date.now() < parseInt(raw, 10);
    } catch (_) {
        return false;
    }
};

const remember = () => {
    try {
        localStorage.setItem(STORAGE_KEY, String(Date.now() + REMEMBER_DAYS * 24 * 60 * 60 * 1000));
    } catch (_) {}
};

const GeoModal = () => {
    const [visible, setVisible] = useState(false);
    const goRef = useRef(null);

    useEffect(() => {
        if (!GEO_ENABLED) return;

        // Test override: append ?geo=AE (or any code) to force detection and
        // ignore any prior dismissal. Harmless in production, affects only the
        // person who adds the param; page content and SEO are unchanged.
        try {
            const override = new URLSearchParams(window.location.search).get('geo');
            if (override !== null) {
                if (CFG.shouldOffer(override.toUpperCase())) setVisible(true);
                return;
            }
        } catch (_) {}

        if (alreadyChosen()) return;

        let cancelled = false;

        const decide = (country) => {
            if (cancelled) return;
            if (CFG.shouldOffer(country)) setVisible(true);
        };

        // Session-cache the country so navigation doesn't refetch.
        let cached = null;
        try { cached = sessionStorage.getItem('rscg_geo_country'); } catch (_) {}

        if (cached !== null) {
            decide(cached);
            return;
        }

        fetch('/geo', { headers: { accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : { country: '' }))
            .then((data) => {
                const country = (data && data.country) || '';
                try { sessionStorage.setItem('rscg_geo_country', country); } catch (_) {}
                decide(country);
            })
            .catch(() => { /* geo unavailable (e.g. local dev): fail safe, no modal */ });

        return () => { cancelled = true; };
    }, []);

    const close = useCallback(() => {
        remember();
        setVisible(false);
    }, []);

    const goToTarget = useCallback(() => {
        remember();
        window.location.href = CFG.targetUrl;
    }, []);

    // Body scroll lock + focus + Escape handling while open.
    useEffect(() => {
        if (!visible) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        if (goRef.current) goRef.current.focus();
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', onKey);
        };
    }, [visible, close]);

    if (!visible) return null;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 lg:p-8"
            style={{ background: 'rgba(5,12,16,0.75)' }}
            onClick={(e) => { if (e.target === e.currentTarget) close(); }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="geo-modal-heading"
        >
            <div className="bg-white max-w-lg w-full relative">
                <button
                    onClick={close}
                    className="absolute top-5 right-5 font-secondary text-[0.6rem] tracking-[0.2em] uppercase text-mc-muted hover:text-mc-dark transition-colors"
                    aria-label="Close"
                >
                    Close ×
                </button>

                <div className="p-10">
                    <div className="w-8 h-px bg-mc-teal mb-6" />

                    <h2
                        id="geo-modal-heading"
                        className="font-primary font-light text-mc-dark mb-4"
                        style={{ fontSize: 'clamp(1.5rem, 3vw, 1.9rem)', letterSpacing: '-0.015em', lineHeight: 1.2 }}
                    >
                        {CFG.heading}
                    </h2>

                    <p className="font-secondary text-mc-muted text-sm leading-relaxed mb-8">
                        {CFG.body}
                    </p>

                    <div className="flex flex-col gap-3">
                        <button
                            ref={goRef}
                            onClick={goToTarget}
                            className="w-full font-secondary text-[0.62rem] tracking-[0.3em] uppercase bg-mc-teal text-white py-4 hover:bg-mc-teal-mid transition-colors duration-300"
                        >
                            {CFG.goLabel} →
                        </button>
                        <button
                            onClick={close}
                            className="w-full font-secondary text-[0.62rem] tracking-[0.3em] uppercase border border-mc-charcoal/20 text-mc-charcoal py-4 hover:border-mc-teal hover:text-mc-teal transition-colors duration-300"
                        >
                            {CFG.stayLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GeoModal;
